import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {convertAttachmentsToUserRequestParts} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {getDefaultAttachments} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import {resolveChatMediaUrl} from '@Libraries/ai-chat.library';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {MediaItemSizeType} from '@Libraries/s3-library';
import type {ChatMessagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {SurveyFieldKind} from './survey.types';
import type {SurveyProps, SurveyFormValues} from './survey.types';
import {buildResponse, emptyValueForFieldKind, otherKey, presentAnswer, presentText} from './survey.library';
import {FieldRenderer} from './fields/field-renderer';
import styles from './survey.module.scss';

export function Survey({spec, onSubmit, onCancel, isSubmitting = false}: SurveyProps): ReactElement {
  const methods = useForm<SurveyFormValues>({
    mode: 'onChange',
    shouldUnregister: false,
  });
  const [stepIndex, setStepIndex] = useState(0);
  const {state} = useAiChat();

  const footerRef = useRef<HTMLDivElement>(null);
  const fieldSlotRef = useRef<HTMLDivElement>(null);
  const promptBoxId = `survey-${spec.surveyId}`;
  const promptBoxConfig = useMemo(() => ({size: AiPromptBoxSize.MEDIUM, attachments: getDefaultAttachments()}), []);
  const surveyPromptBox = useAiPromptBox(promptBoxId, promptBoxConfig);
  const totalSteps = spec.steps.length;
  const currentStep = spec.steps[stepIndex];
  const isLastStep = stepIndex === totalSteps - 1;
  const isFirstStep = stepIndex === 0;
  const isMultiStep = totalSteps > 1;
  const isTextStep = currentStep.field.kind === SurveyFieldKind.TEXT;

  useEffect(() => {
    const slot = fieldSlotRef.current;

    if (slot === null) {
      return;
    }

    const focusable = slot.querySelector<HTMLElement>('input, textarea, [tabindex]');

    if (focusable === null) {
      slot.focus();
      return;
    }

    focusable.focus();
  }, [stepIndex]);

  const watchedValue = useWatch({control: methods.control, name: currentStep.key});
  const watchedOther = useWatch({control: methods.control, name: otherKey(currentStep.key)});
  const hasAnswer = presentAnswer(watchedValue) !== undefined || presentText(watchedOther) !== undefined;
  const isActionDisabled = currentStep.required !== false && !hasAnswer;

  const buildAttachmentParts = (): ChatMessagePart[] => convertAttachmentsToUserRequestParts(surveyPromptBox.state.attachments?.items ?? []);

  const advanceOrSubmit = (): void => {
    if (isLastStep) {
      onSubmit(buildResponse(methods.getValues(), spec.steps), buildAttachmentParts());
      return;
    }

    setStepIndex((i) => i + 1);
  };

  const goNext = async (): Promise<void> => {
    const valid = await methods.trigger(currentStep.key);

    if (!valid) {
      return;
    }

    advanceOrSubmit();
  };

  const goBack = (): void => {
    if (isFirstStep) {
      return;
    }
    setStepIndex((i) => i - 1);
  };

  const skipStep = (): void => {
    methods.resetField(currentStep.key);
    methods.resetField(otherKey(currentStep.key));
    advanceOrSubmit();
  };

  const renderStepImage = (): ReactElement | null => {
    if (currentStep.imageUrl === undefined || currentStep.imageUrl === '') {
      return null;
    }
    return (
      <div className={styles.stepImage}>
        <img className={styles.stepImageImg} src={resolveChatMediaUrl(currentStep.imageUrl, MediaItemSizeType.THUMB)} alt="" />
      </div>
    );
  };

  const renderStepper = (): ReactElement | null => {
    if (!isMultiStep) {
      return null;
    }
    return (
      <div className={styles.stepper}>
        <Button
          size={Size.SMALL}
          type={Type.GHOST}
          icon="icon-caret-left"
          customClasses={styles.navBtn}
          title={window.i18next.t('pmwjs_back')}
          disabled={isFirstStep || isSubmitting}
          onClick={goBack}
        />
        <Text size={TextSize.SMALL} val={`${stepIndex + 1} / ${totalSteps}`} className={styles.stepCount} attributes={{role: 'status'}} />
        <Button
          size={Size.SMALL}
          type={Type.GHOST}
          icon="icon-caret-right"
          customClasses={styles.navBtn}
          title={window.i18next.t('pmwjs_next')}
          disabled={isLastStep || isSubmitting}
          onClick={(): void => {
            void goNext();
          }}
        />
      </div>
    );
  };

  const renderAttachments = (): ReactElement | null => {
    if (!isTextStep) {
      return null;
    }
    return <AiPromptBoxAttachments id={promptBoxId} />;
  };

  const renderFooterLeft = (): ReactElement => {
    if (isTextStep) {
      // Open-ended step: the Ai prompt box uploads control (same circular "+" + full upload dropdown),
      // action buttons pushed right.
      return (
        <>
          <AiPromptBoxUploads promptBoxId={promptBoxId} footerRef={footerRef} renderAttachmentTooltip />
          <div className={styles.spacer} />
        </>
      );
    }
    const placeholder = window.i18next.t('pmwjs_something_else');
    return (
      <Controller
        control={methods.control}
        name={otherKey(currentStep.key)}
        defaultValue=""
        render={({field: rhfField}): ReactElement => (
          <InputField
            className={styles.somethingElse}
            inputClassName="_borderless"
            iconClassName={styles.somethingElseIcon}
            hasIcon="icon-pencil"
            size={InputFieldSize.SMALL}
            placeholder={placeholder}
            elementTitle={placeholder}
            input={typeof rhfField.value === 'string' ? rhfField.value : ''}
            isDisabled={isSubmitting}
            onInputChange={(val): void => {
              const text = String(val);
              rhfField.onChange(text);
              if (text !== '') {
                methods.setValue(currentStep.key, emptyValueForFieldKind(currentStep.field.kind), {shouldValidate: true});
              }
            }}
            onInputBlur={(): void => {
              rhfField.onBlur();
            }}
          />
        )}
      />
    );
  };

  return (
    <FormProvider {...methods}>
      <div className={state.isSmall ? `${styles.root} ${styles.small}` : styles.root}>
        {renderStepImage()}
        <div className={styles.header}>
          <Text size={TextSize.SMALL} val={currentStep.helpText} className={styles.label} />
          <div className={styles.headerControls}>
            {renderStepper()}
            <Button
              size={Size.SMALL}
              type={Type.GHOST}
              icon="icon-close"
              customClasses={styles.navBtn}
              title={window.i18next.t('pmwjs_cancel')}
              disabled={isSubmitting}
              onClick={onCancel}
            />
          </div>
        </div>

        <div className={isTextStep ? styles.contentFlush : styles.content}>
          {renderAttachments()}
          <div key={currentStep.key} ref={fieldSlotRef} tabIndex={-1} className={styles.fieldSlot}>
            <FieldRenderer step={currentStep} />
          </div>
        </div>

        <div className={styles.footer} ref={footerRef}>
          {renderFooterLeft()}
          <div className={styles.footerRight}>
            <Button size={Size.SMALL} type={Type.GHOST} text={window.i18next.t('pmwjs_skip')} disabled={isSubmitting} onClick={skipStep} />
            <Button
              size={Size.SMALL}
              type={isLastStep ? Type.AI : Type.PRIMARY}
              iconPlacement={IconPlacement.RIGHT}
              icon="icon-arrow-right"
              text={isLastStep ? (spec.submitLabel ?? window.i18next.t('pmwjs_done')) : window.i18next.t('pmwjs_next')}
              disabled={isActionDisabled || isSubmitting}
              onClick={(): void => {
                void goNext();
              }}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
